import React, { createContext, useContext, useState, useEffect } from "react";

export interface CartItem {
  id: string;
  name: string;
  price: number;
  categories: string[];
  minOrder: number;
  companyId: string;
  unit: string;
  images: string[];
  store: {
    name: string;
    city?: string;
    region?: string;
  };
  quantity: number;
  note?: string;
}

interface CartContextType {
  cartItems: CartItem[];
  addToCart: (item: CartItem) => void;
  removeFromCart: (itemId: string) => void;
  clearCart: () => void;
  updateQuantity: (itemId: string, quantity: number) => void;
  totalItems: number;
  totalPrice: number;
  inquiryItems: CartItem[];
  addToInquiry: (item: CartItem) => void;
  removeFromInquiry: (itemId: string) => void;
  updateInquiryItem: (itemId: string, updates: Partial<CartItem>) => void;
  clearInquiry: () => void;
  isInInquiry: (itemId: string) => boolean;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [inquiryItems, setInquiryItems] = useState<CartItem[]>([]);

  // Load cart from localStorage on initial render
  useEffect(() => {
    const savedCart = localStorage.getItem("marketplaceCart");
    if (savedCart) {
      setCartItems(JSON.parse(savedCart));
    }
  }, []);

  // Load inquiry items from localStorage on initial render
  useEffect(() => {
    const savedInquiry = localStorage.getItem("marketplaceInquiry");
    if (savedInquiry) {
      setInquiryItems(JSON.parse(savedInquiry));
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("marketplaceCart", JSON.stringify(cartItems));
  }, [cartItems]);

  // Save inquiry items to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem("marketplaceInquiry", JSON.stringify(inquiryItems));
  }, [inquiryItems]);

  const addToCart = (item: CartItem) => {
    setCartItems((prevItems) => {
      const existingItem = prevItems.find((i) => i.id === item.id);
      if (existingItem) {
        return prevItems.map((i) =>
          i.id === item.id ? { ...i, quantity: i.quantity + 1 } : i
        );
      }
      return [...prevItems, { ...item, quantity: item.quantity }];
    });
  };

  const removeFromCart = (itemId: string) => {
    setCartItems((prevItems) => prevItems.filter((item) => item.id !== itemId));
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const updateQuantity = (itemId: string, quantity: number) => {
    if (quantity < 1) return;
    setCartItems((prevItems) =>
      prevItems.map((item) =>
        item.id === itemId ? { ...item, quantity } : item
      )
    );
  };

  const totalItems = cartItems.reduce((total) => total + 1, 0);
  const totalPrice = cartItems.reduce(
    (total, item) => total + item.price * item.quantity,
    0
  );

  // Inquiry methods
  const addToInquiry = (item: CartItem) => {
    setInquiryItems((prev) => {
      if (prev.some((i) => i.id === item.id)) return prev;
      return [...prev, { ...item, note: "" }];
    });
  };

  const removeFromInquiry = (itemId: string) => {
    setInquiryItems((prev) => prev.filter((item) => item.id !== itemId));
  };

  const updateInquiryItem = (itemId: string, updates: Partial<CartItem>) => {
    setInquiryItems((prev) =>
      prev.map((item) => (item.id === itemId ? { ...item, ...updates } : item))
    );
  };

  const clearInquiry = () => {
    setInquiryItems([]);
  };

  const isInInquiry = (itemId: string) => {
    return inquiryItems.some((item) => item.id === itemId);
  };

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        removeFromCart,
        clearCart,
        updateQuantity,
        totalItems,
        totalPrice,
        inquiryItems,
        addToInquiry,
        removeFromInquiry,
        updateInquiryItem,
        clearInquiry,
        isInInquiry,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
}
